import React, { useEffect, useMemo, useState } from "react";
import { Link, useOutletContext, useParams } from "react-router-dom";
import { BellRing, CheckCircle2, KeyRound, Loader2, MapPin, Save, ShieldCheck, UserRound, X } from "lucide-react";
import PartnerPageHeader from "@/components/partners/PartnerPageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "@/components/ui/use-toast";
import { getPartnerHeaders } from "@/integrations/php/client";
import { PartnerOutletContext, PartnerProfile, SERVICE_LABELS } from "@/types/partnerPortal";

interface PartnerSettingsForm {
  name: string;
  email: string;
  phone: string;
  location: string;
  radius: number;
  smsOptOut: boolean;
}

const toForm = (partner: PartnerProfile): PartnerSettingsForm => ({
  name: partner.name,
  email: partner.email,
  phone: partner.phone,
  location: partner.location,
  radius: partner.radius || 10,
  smsOptOut: Boolean(partner.sms_opt_out),
});

const PartnerSettingsPage = () => {
  const { partnerId } = useParams<{ partnerId: string }>();
  const { partner, updatePartner } = useOutletContext<PartnerOutletContext>();
  const [form, setForm] = useState<PartnerSettingsForm>(() => toForm(partner));
  const [savedForm, setSavedForm] = useState<PartnerSettingsForm>(() => toForm(partner));
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const next = toForm(partner);
    setForm(next);
    setSavedForm(next);
  }, [partner]);

  const hasChanges = useMemo(() => JSON.stringify(form) !== JSON.stringify(savedForm), [form, savedForm]);
  const completeness = [form.name, form.email, form.phone, form.location].filter(Boolean).length * 25;

  const updateField = <K extends keyof PartnerSettingsForm>(field: K, value: PartnerSettingsForm[K]) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const validate = () => {
    if (form.name.trim().length < 3) return "Το όνομα πρέπει να έχει τουλάχιστον 3 χαρακτήρες.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return "Συμπληρώστε έγκυρο email.";
    if (!/^[0-9+() .-]{10,30}$/.test(form.phone)) return "Συμπληρώστε έγκυρο τηλέφωνο.";
    if (form.location.trim().length < 3) return "Συμπληρώστε έγκυρη τοποθεσία.";
    if (!Number.isFinite(form.radius) || form.radius < 1 || form.radius > 200) return "Η ακτίνα πρέπει να είναι από 1 έως 200 km.";
    return null;
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const validationError = validate();
    if (validationError) {
      toast({ title: "Ελέγξτε τα στοιχεία", description: validationError, variant: "destructive" });
      return;
    }
    if (!partnerId) return;

    setLoading(true);
    try {
      const response = await fetch("/api/update-partner.php", {
        method: "POST",
        credentials: "include",
        headers: getPartnerHeaders(),
        body: JSON.stringify({
          partner_id: partnerId,
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          location: form.location.trim(),
          radius: form.radius,
          sms_opt_out: form.smsOptOut,
        }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok || !body.success) throw new Error(body.error || "Η ενημέρωση απέτυχε.");

      const normalized = { ...form, name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim(), location: form.location.trim() };
      setForm(normalized);
      setSavedForm(normalized);
      updatePartner({ ...partner, name: normalized.name, email: normalized.email, phone: normalized.phone, location: normalized.location, radius: normalized.radius, sms_opt_out: normalized.smsOptOut });
      toast({ title: "Οι αλλαγές αποθηκεύτηκαν", description: "Το προφίλ σας είναι ενημερωμένο." });
    } catch (caught) {
      toast({ title: "Δεν αποθηκεύτηκαν οι αλλαγές", description: caught instanceof Error ? caught.message : "Δοκιμάστε ξανά.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-6">
      <PartnerPageHeader
        eyebrow="Λογαριασμός"
        title="Ρυθμίσεις"
        description="Κρατήστε σωστά τα στοιχεία επικοινωνίας, την περιοχή κάλυψης και τις ειδοποιήσεις σας."
      />

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_360px]">
        <div className="space-y-5">
          <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-sky-50 text-sky-700"><UserRound className="h-5 w-5" /></div>
              <div><h2 className="font-bold text-slate-950">Στοιχεία συνεργάτη</h2><p className="mt-1 text-xs leading-5 text-slate-500">Χρησιμοποιούνται στις ειδοποιήσεις και στο portal.</p></div>
            </div>
            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              <div className="space-y-2"><Label htmlFor="partner-name">Επωνυμία / όνομα</Label><Input id="partner-name" value={form.name} onChange={(event) => updateField("name", event.target.value)} disabled={loading} className="h-11 rounded-xl" autoComplete="organization" /></div>
              <div className="space-y-2"><Label htmlFor="partner-email">Email</Label><Input id="partner-email" type="email" value={form.email} onChange={(event) => updateField("email", event.target.value)} disabled={loading} className="h-11 rounded-xl" autoComplete="email" /></div>
              <div className="space-y-2"><Label htmlFor="partner-phone">Τηλέφωνο</Label><Input id="partner-phone" inputMode="tel" value={form.phone} onChange={(event) => updateField("phone", event.target.value)} disabled={loading} className="h-11 rounded-xl" autoComplete="tel" /></div>
              <div className="space-y-2"><Label htmlFor="partner-location">Έδρα / τοποθεσία</Label><Input id="partner-location" value={form.location} onChange={(event) => updateField("location", event.target.value)} disabled={loading} className="h-11 rounded-xl" autoComplete="address-level2" /></div>
            </div>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6">
            <div className="flex items-start gap-3"><div className="flex h-10 w-10 items-center justify-center rounded-xl bg-violet-50 text-violet-700"><MapPin className="h-5 w-5" /></div><div><h2 className="font-bold text-slate-950">Περιοχή κάλυψης</h2><p className="mt-1 text-xs leading-5 text-slate-500">Η αυτόματη αντιστοίχιση εξετάζει την απόσταση από την έδρα σας.</p></div></div>
            <div className="mt-5 max-w-sm space-y-2"><Label htmlFor="partner-radius">Ακτίνα αντιστοίχισης (km)</Label><Input id="partner-radius" type="number" min={1} max={200} value={form.radius} onChange={(event) => updateField("radius", Number(event.target.value))} disabled={loading} className="h-11 rounded-xl" /><p className="text-xs leading-5 text-slate-500">Τα leads εκτός ακτίνας δεν σας ανατίθενται αυτόματα.</p></div>
            <div className="mt-5 border-t border-slate-100 pt-5"><p className="text-xs font-bold uppercase tracking-wider text-slate-500">Ενεργές υπηρεσίες</p><div className="mt-3 flex flex-wrap gap-2">{partner.services.length ? partner.services.map((service) => <span key={service} className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-xs font-semibold text-slate-700">{SERVICE_LABELS[service] || service}</span>) : <span className="text-sm text-slate-500">Δεν έχουν οριστεί υπηρεσίες.</span>}</div><p className="mt-3 text-xs text-slate-500">Για αλλαγή υπηρεσιών επικοινωνήστε με τη Διαθερμική.</p></div>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6">
            <div className="flex items-start gap-3"><div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-50 text-amber-700"><BellRing className="h-5 w-5" /></div><div><h2 className="font-bold text-slate-950">Ειδοποιήσεις</h2><p className="mt-1 text-xs leading-5 text-slate-500">Τα email νέων αναθέσεων παραμένουν ενεργά.</p></div></div>
            <div className="mt-5 flex items-start justify-between gap-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div><Label htmlFor="sms-notifications" className="text-sm font-bold text-slate-900">SMS για νέα leads</Label><p className="mt-1 max-w-xl text-xs leading-5 text-slate-500">{form.smsOptOut ? "Τα SMS είναι απενεργοποιημένα. Θα συνεχίσετε να λαμβάνετε email." : "Θα λαμβάνετε SMS όταν σας ανατίθεται νέο lead. Τα νυχτερινά SMS αποστέλλονται μετά τις 08:00."}</p></div>
              <Switch id="sms-notifications" checked={!form.smsOptOut} onCheckedChange={(checked) => updateField("smsOptOut", !checked)} disabled={loading} className="shrink-0" />
            </div>
          </section>
        </div>

        <aside className="space-y-5">
          <section className="rounded-2xl border border-slate-200 bg-white p-5">
            <div className="flex items-center justify-between"><h2 className="font-bold text-slate-950">Πληρότητα προφίλ</h2><span className="text-sm font-bold text-emerald-700">{completeness}%</span></div>
            <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-emerald-500" style={{ width: `${completeness}%` }} /></div>
            <p className="mt-3 flex items-start gap-2 text-xs leading-5 text-slate-500"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />Τα σωστά στοιχεία βοηθούν στην ορθή δρομολόγηση και επικοινωνία.</p>
          </section>
          <section className="rounded-2xl border border-slate-200 bg-white p-5">
            <div className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-sky-700" /><h2 className="font-bold text-slate-950">Ασφάλεια λογαριασμού</h2></div>
            <p className="mt-2 text-xs leading-5 text-slate-500">Αν θέλετε νέο κωδικό, θα λάβετε ασφαλή σύνδεσμο μίας χρήσης στο email σας.</p>
            <Button asChild variant="outline" className="mt-4 h-11 w-full rounded-xl"><Link to="/partner/password-reset"><KeyRound className="mr-2 h-4 w-4" /> Αλλαγή κωδικού</Link></Button>
          </section>
        </aside>
      </div>

      {hasChanges && (
        <div className="sticky bottom-[4.75rem] z-20 rounded-2xl border border-slate-200 bg-white/95 p-3 shadow-lg backdrop-blur md:bottom-4">
          <div className="flex items-center justify-between gap-3">
            <p className="hidden text-sm text-slate-600 sm:block">Έχετε αλλαγές που δεν έχουν αποθηκευτεί.</p>
            <div className="grid w-full grid-cols-2 gap-2 sm:ml-auto sm:w-auto">
              <Button type="button" variant="outline" onClick={() => setForm(savedForm)} disabled={loading} className="h-11 rounded-xl"><X className="mr-2 h-4 w-4" /> Ακύρωση</Button>
              <Button type="submit" disabled={loading} className="h-11 rounded-xl bg-sky-600 hover:bg-sky-700">{loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Αποθήκευση</Button>
            </div>
          </div>
        </div>
      )}
    </form>
  );
};

export default PartnerSettingsPage;
